"use client";

import { api } from "convex/_generated/api";
import type { Id } from "convex/_generated/dataModel";
import { useAction } from "convex/react";
import { Loader2, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";

type Props = {
  testId: Id<"testGenerations">;
  sectionIndex: number;
  taskIndex?: number;
  /** Short label shown at the top of the expanded panel, e.g. "I. COMPREHENSION" */
  contextLabel: string;
  /** Whether the panel is currently open — controlled by the parent */
  isOpen: boolean;
  onClose: () => void;
  onRefined: (updated: unknown) => void;
  onLoadingChange?: (loading: boolean) => void;
};

const PLACEHOLDER =
  "Describe what you want — or attach a sample image to show the format you'd like.\ne.g. Use a table instead, make it bullet points, add a word box...";

type SampleImage = {
  id: string;
  file: File;
  base64: string;
  previewUrl: string;
};

const MAX_IMAGES = 2;
const MAX_IMAGE_BYTES = 2 * 1024 * 1024;
const MAX_IMAGE_DIMENSION = 1800;

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsDataURL(file);
  });
}

function canvasToBlob(
  canvas: HTMLCanvasElement,
  mimeType: string,
  quality: number,
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error("Failed to compress image"));
          return;
        }
        resolve(blob);
      },
      mimeType,
      quality,
    );
  });
}

async function compressImageForRefinement(file: File): Promise<File> {
  const bitmap = await createImageBitmap(file);
  let width = bitmap.width;
  let height = bitmap.height;
  const longestSide = Math.max(width, height);
  if (longestSide > MAX_IMAGE_DIMENSION) {
    const scale = MAX_IMAGE_DIMENSION / longestSide;
    width = Math.max(1, Math.round(width * scale));
    height = Math.max(1, Math.round(height * scale));
  }

  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");
  if (!context) {
    bitmap.close();
    throw new Error("Failed to process image");
  }

  const qualities = [0.9, 0.8, 0.7, 0.6, 0.5, 0.4];
  let currentWidth = width;
  let currentHeight = height;
  for (let pass = 0; pass < 3; pass += 1) {
    canvas.width = currentWidth;
    canvas.height = currentHeight;
    context.clearRect(0, 0, currentWidth, currentHeight);
    context.drawImage(bitmap, 0, 0, currentWidth, currentHeight);
    for (const quality of qualities) {
      const blob = await canvasToBlob(canvas, "image/webp", quality);
      if (blob.size <= MAX_IMAGE_BYTES) {
        bitmap.close();
        const originalStem = file.name.replace(/\.[^/.]+$/, "");
        const outputType =
          blob.type && blob.type.startsWith("image/") ? blob.type : file.type;
        const extension =
          outputType === "image/png"
            ? "png"
            : outputType === "image/jpeg"
              ? "jpg"
              : "webp";
        return new File([blob], `${originalStem}.${extension}`, {
          type: outputType,
          lastModified: Date.now(),
        });
      }
    }
    currentWidth = Math.max(1, Math.round(currentWidth * 0.8));
    currentHeight = Math.max(1, Math.round(currentHeight * 0.8));
  }

  bitmap.close();
  throw new Error("Image is too large to process. Please try another image.");
}

export function TestSectionRefinement({
  testId,
  sectionIndex,
  taskIndex,
  contextLabel,
  isOpen,
  onClose,
  onRefined,
  onLoadingChange,
}: Props) {
  const refine = useAction(api.actions.refineTestSection.refineTestSection);
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(false);
  const [sampleImages, setSampleImages] = useState<SampleImage[]>([]);
  const sampleImagesRef = useRef<SampleImage[]>([]);

  useEffect(() => {
    sampleImagesRef.current = sampleImages;
  }, [sampleImages]);

  useEffect(() => {
    return () => {
      for (const image of sampleImagesRef.current) {
        URL.revokeObjectURL(image.previewUrl);
      }
    };
  }, []);

  const canSubmit =
    (note.trim().length > 0 || sampleImages.length > 0) && !loading && !cooldown;

  async function handleImageSelect(event: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(event.target.files ?? []);
    if (!files.length) {
      return;
    }
    event.target.value = "";
    setError(null);

    const remaining = MAX_IMAGES - sampleImages.length;
    const toProcess = files.slice(0, remaining);
    for (const file of toProcess) {
      if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
        setError(`"${file.name}" is not supported. Use JPG, PNG, or WEBP.`);
        continue;
      }
      try {
        const compressedFile = await compressImageForRefinement(file);
        const base64 = await fileToBase64(compressedFile);
        const previewUrl = URL.createObjectURL(compressedFile);
        setSampleImages((prev) => [
          ...prev,
          { id: crypto.randomUUID(), file: compressedFile, base64, previewUrl },
        ]);
        setError(null);
      } catch {
        setError(`"${file.name}" is too large. We could not compress it under 2 MB.`);
      }
    }
  }

  function removeImage(id: string) {
    setSampleImages((prev) => {
      const image = prev.find((entry) => entry.id === id);
      if (image) {
        URL.revokeObjectURL(image.previewUrl);
      }
      return prev.filter((entry) => entry.id !== id);
    });
  }

  function clearSampleImages() {
    setSampleImages((prev) => {
      for (const image of prev) {
        URL.revokeObjectURL(image.previewUrl);
      }
      return [];
    });
  }

  async function handleSubmit() {
    if (!canSubmit) return;
    setLoading(true);
    setError(null);
    onLoadingChange?.(true);
    try {
      const updated = await refine({
        testId,
        sectionIndex,
        taskIndex,
        userNote: note.trim(),
        sampleImageBase64: sampleImages.map((image) => image.base64),
      });
      onRefined(updated);
      setNote("");
      clearSampleImages();
      setError(null);
      onClose();
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : "Could not apply changes. Please try again.";
      setError(message || "Could not apply changes. Please try again.");
    } finally {
      setLoading(false);
      setCooldown(true);
      onLoadingChange?.(false);
      window.setTimeout(() => setCooldown(false), 1000);
    }
  }

  if (!isOpen) return null;

  return (
    <div className="mt-2 rounded-xl border border-border bg-card shadow-sm">
      {/* Panel header */}
      <div className="flex items-center justify-between border-b border-border px-3 py-2">
        <p className="text-xs font-medium text-foreground/80">
          Editing:{" "}
          <span className="font-semibold text-foreground">{contextLabel}</span>
        </p>
        <button
          type="button"
          onClick={() => {
            if (loading) return;
            setError(null);
            clearSampleImages();
            onClose();
          }}
          disabled={loading}
          className="rounded p-1 text-muted-foreground transition-colors hover:text-foreground disabled:opacity-40"
          title="Cancel"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Panel body */}
      <div className="p-3">
        <textarea
          rows={3}
          value={note}
          onChange={(event) => setNote(event.target.value)}
          placeholder={PLACEHOLDER}
          disabled={loading}
          autoFocus
          className="w-full resize-none rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none ring-primary/20 transition focus:ring-2 disabled:cursor-not-allowed disabled:opacity-60"
        />
        <div className="mt-3 space-y-2">
          <p className="text-xs font-medium text-gray-500">
            📎 Attach sample exercise (optional)
          </p>
          {sampleImages.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {sampleImages.map((image) => (
                <div
                  key={image.id}
                  className="group relative h-16 w-16 overflow-hidden rounded-md border border-gray-200"
                >
                  <img
                    src={image.previewUrl}
                    alt={image.file.name}
                    className="h-full w-full object-cover"
                  />
                  <button
                    type="button"
                    onClick={() => removeImage(image.id)}
                    className="absolute right-0.5 top-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-black/60 text-[10px] text-white opacity-0 transition-opacity group-hover:opacity-100"
                    aria-label="Remove image"
                    disabled={loading}
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          ) : null}
          {sampleImages.length < MAX_IMAGES ? (
            <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-dashed border-gray-300 px-3 py-1.5 text-xs text-gray-500 transition-colors hover:border-gray-400 hover:text-gray-700">
              <span>+ Add image</span>
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                multiple
                className="hidden"
                onChange={(event) => void handleImageSelect(event)}
                disabled={loading}
              />
            </label>
          ) : null}
          {error ? <p className="text-xs text-red-600">{error}</p> : null}
        </div>
        <div className="mt-2 flex items-center justify-end">
          <button
            type="button"
            disabled={!canSubmit}
            onClick={() => void handleSubmit()}
            style={!canSubmit ? undefined : { backgroundColor: "#2A6B4F" }}
            className="inline-flex min-h-9 items-center gap-1.5 rounded-lg bg-muted px-4 text-xs font-semibold text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:bg-muted disabled:text-muted-foreground"
          >
            {loading ? (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Applying…
              </>
            ) : (
              "Refine ↗"
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
